import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  RefreshCw, Search, Download, ChevronLeft, ChevronRight,
  Clock, CheckCircle, AlertTriangle, Loader2, FileText, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  FINALIZADA: 'bg-green-600',
  AGUARDANDO_ANALISE: 'bg-yellow-500',
  EM_TRATAMENTO: 'bg-blue-500',
  AGUARDANDO_ANALISE_TERCEIROS: 'bg-purple-500',
  AGUARDANDO_PROXIMA_ANALISE: 'bg-orange-500',
  AGUARDANDO_ANALISE_N2: 'bg-red-500',
};

const STATUS_LABELS = {
  FINALIZADA: 'Finalizada',
  AGUARDANDO_ANALISE: 'Aguardando Análise',
  EM_TRATAMENTO: 'Em Tratamento',
  AGUARDANDO_ANALISE_TERCEIROS: 'Aguardando Terceiros',
  AGUARDANDO_PROXIMA_ANALISE: 'Próxima Análise',
  AGUARDANDO_ANALISE_N2: 'Análise N2',
};

const ITEMS_PER_PAGE = 20;

export default function BackofficeClaro() {
  const { user } = useAuth();
  const [tratativas, setTratativas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [renovando, setRenovando] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [agentes, setAgentes] = useState([]);
  const [detalhe, setDetalhe] = useState(null);

  const token = localStorage.getItem('token');

  const carregarTratativas = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API}/api/conexao-claro/tratativas/todas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `Erro ${resp.status}`);
      }
      const data = await resp.json();
      setTratativas(data.tratativas || []);
      setTotalItems(data.total || 0);
      setAgentes(data.agentes || []);
      toast.success(`${data.total} tratativas carregadas do cache`);
    } catch (error) {
      console.error('Erro:', error);
      toast.error(error.message || 'Erro ao carregar tratativas');
    } finally {
      setLoading(false);
    }
  };

  const sincronizar = async () => {
    setRenovando(true);
    toast.info('Sincronizando com Conexão Claro (pode levar alguns minutos)...');
    try {
      const resp = await fetch(`${API}/api/conexao-claro/sincronizar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Falha ao sincronizar');
      const data = await resp.json();
      if (data.contas_ok?.length > 0) {
        toast.success(`${data.novas} novas tratativas de ${data.contas_ok.length} conta(s). Total: ${data.total_cache}`);
      } else {
        toast.warning(data.message || 'Nenhuma conta ativa encontrada');
      }
      if (data.contas_erro?.length > 0) {
        toast.error(`Contas com erro: ${data.contas_erro.join(', ')}`);
      }
      await carregarTratativas();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRenovando(false);
    }
  };

  const renovarToken = async () => {
    setRenovando(true);
    try {
      const resp = await fetch(`${API}/api/conexao-claro/renovar-token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Falha ao renovar token');
      toast.success('Token renovado com sucesso!');
      await carregarTratativas();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRenovando(false);
    }
  };

  useEffect(() => {
    carregarTratativas();
  }, []);

  // Normalize text for accent-insensitive search
  const normalize = (str) => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  const filtradas = useMemo(() => {
    let items = [...tratativas];
    if (filtroStatus) {
      items = items.filter(t => t.status === filtroStatus);
    }
    if (busca.trim()) {
      const term = normalize(busca);
      items = items.filter(t =>
        normalize(t.numeroProposta).includes(term) ||
        normalize(t.protocolo).includes(term) ||
        normalize(t.nomeCliente).includes(term) ||
        normalize(t.cpfCnpj).includes(term) ||
        normalize(t.usuarioNome).includes(term) ||
        normalize(t.descricaoTratativa).includes(term) ||
        normalize(t.cidade).includes(term) ||
        normalize(t.uf).includes(term)
      );
    }
    return items;
  }, [tratativas, filtroStatus, busca]);

  const totalPages = Math.ceil(filtradas.length / ITEMS_PER_PAGE);
  const paginadas = filtradas.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);

  // Statistics
  const stats = useMemo(() => {
    const s = {};
    tratativas.forEach(t => {
      s[t.status] = (s[t.status] || 0) + 1;
    });
    return s;
  }, [tratativas]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const exportarCSV = () => {
    const headers = ['Protocolo', 'Proposta', 'Cliente', 'CPF/CNPJ', 'Status', 'Tipo Serviço', 'Tratativa', 'Vendedor', 'Cidade', 'UF', 'Data Criação', 'Processo'];
    const rows = filtradas.map(t => [
      t.protocolo, t.numeroProposta, t.nomeCliente, t.cpfCnpj,
      STATUS_LABELS[t.status] || t.status, t.descricaoTipoServico,
      t.descricaoTratativa, t.usuarioNome, t.cidade || '', t.uf || '',
      formatDate(t.dataCriacao), t.processo
    ]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `backoffice_claro_${new Date().getTime()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exportado!');
  };

  return (
    <div className="space-y-4" data-testid="backoffice-claro-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Backoffice Claro</h1>
          <p className="text-sm text-gray-400">
            Tratativas extraídas do portal Conexão Claro Brasil
            {agentes.length > 0 && ` • ${agentes.map(a => a.razao).join(', ')}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={sincronizar}
            variant="outline"
            disabled={renovando}
            className="border-orange-600 text-orange-300 hover:bg-orange-900"
            data-testid="sincronizar-btn"
          >
            {renovando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Sincronizar
          </Button>
          <Button
            onClick={carregarTratativas}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="atualizar-tratativas-btn"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Atualizar
          </Button>
          <Button
            onClick={exportarCSV}
            disabled={filtradas.length === 0}
            className="bg-green-600 hover:bg-green-700"
            data-testid="exportar-csv-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card className="bg-gray-800 border-gray-700 cursor-pointer hover:bg-gray-750" onClick={() => { setFiltroStatus(''); setPage(0); }}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-white">{totalItems}</p>
            <p className="text-xs text-gray-400">Total</p>
          </CardContent>
        </Card>
        {Object.entries(stats).map(([status, count]) => (
          <Card
            key={status}
            className={`bg-gray-800 border-gray-700 cursor-pointer hover:bg-gray-750 ${filtroStatus === status ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => { setFiltroStatus(filtroStatus === status ? '' : status); setPage(0); }}
          >
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-white">{count}</p>
              <p className="text-xs text-gray-400">{STATUS_LABELS[status] || status}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={busca}
            onChange={e => { setBusca(e.target.value); setPage(0); }}
            placeholder="Buscar por protocolo, proposta, cliente, CPF, vendedor, cidade..."
            className="pl-10 bg-gray-800 border-gray-600 text-white"
            data-testid="busca-tratativas"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mr-3" />
          <span className="text-gray-400">Carregando tratativas da Conexão Claro... (pode levar até 2 minutos)</span>
        </div>
      ) : (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tabela-tratativas">
                <thead>
                  <tr className="border-b border-gray-700 text-gray-400 text-xs">
                    <th className="px-3 py-2 text-left">Protocolo</th>
                    <th className="px-3 py-2 text-left">Proposta</th>
                    <th className="px-3 py-2 text-left">Cliente</th>
                    <th className="px-3 py-2 text-left">CPF/CNPJ</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Tratativa</th>
                    <th className="px-3 py-2 text-left">Vendedor</th>
                    <th className="px-3 py-2 text-left">Cidade/UF</th>
                    <th className="px-3 py-2 text-left">Criação</th>
                    <th className="px-3 py-2 text-left">Processo</th>
                    <th className="px-3 py-2 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {paginadas.map((t, idx) => (
                    <tr key={t.tratativaId || idx} className="border-b border-gray-700/50 hover:bg-gray-700/30 text-gray-300">
                      <td className="px-3 py-2 font-mono text-xs">{t.protocolo}</td>
                      <td className="px-3 py-2 font-semibold text-blue-400">{t.numeroProposta}</td>
                      <td className="px-3 py-2 max-w-[150px] truncate">{t.nomeCliente}</td>
                      <td className="px-3 py-2 font-mono text-xs">{t.cpfCnpj}</td>
                      <td className="px-3 py-2">
                        <Badge className={`${STATUS_COLORS[t.status] || 'bg-gray-600'} text-white text-xs`}>
                          {STATUS_LABELS[t.status] || t.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs">{t.descricaoTratativa}</td>
                      <td className="px-3 py-2 text-xs max-w-[120px] truncate">{t.usuarioNome}</td>
                      <td className="px-3 py-2 text-xs">{t.cidade ? `${t.cidade}/${t.uf}` : '-'}</td>
                      <td className="px-3 py-2 text-xs">{formatDate(t.dataCriacao)}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
                          {t.processo}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-gray-400 hover:text-white"
                          onClick={() => setDetalhe(t)}
                          data-testid={`detalhe-btn-${idx}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {paginadas.length === 0 && (
                    <tr>
                      <td colSpan="11" className="px-3 py-8 text-center text-gray-500">
                        {tratativas.length === 0 ? 'Nenhuma tratativa carregada' : 'Nenhum resultado encontrado'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filtradas.length > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
                <span className="text-sm text-gray-400">
                  Mostrando {page * ITEMS_PER_PAGE + 1}-{Math.min((page + 1) * ITEMS_PER_PAGE, filtradas.length)} de {filtradas.length}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                    className="border-gray-600 text-gray-300"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-gray-400 flex items-center px-2">
                    {page + 1} / {totalPages}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(p => p + 1)}
                    className="border-gray-600 text-gray-300"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Detail Modal */}
      {detalhe && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setDetalhe(null)}>
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 border border-gray-700" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-400" />
                Tratativa #{detalhe.tratativaId}
              </h2>
              <Button size="sm" variant="ghost" onClick={() => setDetalhe(null)} className="text-gray-400 hover:text-white">✕</Button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ['Protocolo', detalhe.protocolo],
                ['Proposta', detalhe.numeroProposta],
                ['Cliente', detalhe.nomeCliente],
                ['CPF/CNPJ', detalhe.cpfCnpj],
                ['Status', STATUS_LABELS[detalhe.status] || detalhe.status],
                ['Tratativa', detalhe.descricaoTratativa],
                ['Tipo Serviço', detalhe.descricaoTipoServico],
                ['Processo', detalhe.processo],
                ['Vendedor', detalhe.usuarioNome],
                ['Email Vendedor', detalhe.usuarioEmail],
                ['CPF Vendedor', detalhe.usuarioCpf],
                ['Cidade/UF', `${detalhe.cidade || '-'} / ${detalhe.uf || '-'}`],
                ['Canal Vendas', detalhe.canalVendas],
                ['Data Criação', formatDate(detalhe.dataCriacao)],
                ['Data Tratamento', formatDate(detalhe.dataTratamento)],
                ['Instalação', detalhe.dataInstalacaoAgendaReal || '-'],
                ['Agenda Real', detalhe.agendaReal === 'V' ? 'Sim' : 'Não'],
                ['Dentro do Prazo', detalhe.dentroPrazo === 'V' ? 'Sim' : detalhe.dentroPrazo === 'F' ? 'Não' : '-'],
                ['Prioridade', detalhe.prioridade || '-'],
                ['Fila Workflow', detalhe.filaWorkflow || '-'],
                ['Organização', detalhe.organizacao || '-'],
                ['Fornecedor', detalhe.fornecedor || '-'],
                ['Estrutura', detalhe.estrutura || '-'],
                ['Offshore', detalhe.isOffshore === 'V' ? 'Sim' : 'Não'],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-gray-500 text-xs">{label}</p>
                  <p className="text-gray-200">{value || '-'}</p>
                </div>
              ))}
            </div>
            {(detalhe.detalhePendencia || detalhe.detalheTratamento || detalhe.fraseologia) && (
              <div className="mt-4 space-y-3">
                {detalhe.detalhePendencia && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Detalhe Pendência</p>
                    <p className="text-gray-200 text-sm bg-gray-700/50 p-2 rounded">{detalhe.detalhePendencia}</p>
                  </div>
                )}
                {detalhe.detalheTratamento && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Detalhe Tratamento</p>
                    <p className="text-gray-200 text-sm bg-gray-700/50 p-2 rounded">{detalhe.detalheTratamento}</p>
                  </div>
                )}
                {detalhe.fraseologia && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Fraseologia</p>
                    <p className="text-gray-200 text-sm bg-gray-700/50 p-2 rounded">{detalhe.fraseologia}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
